import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CommonCheckboxComponent } from '@shared/components/common-checkbox/common-checkbox.component';

let dropdownIdCounter = 0;

@Component({
  selector: 'app-common-dropdown',
  standalone: true,
  imports: [CommonModule, CommonCheckboxComponent],
  templateUrl: './common-dropdown.component.html',
  styleUrls: ['./common-dropdown.component.scss'],
})
export class CommonDropdownComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() filterType: 'select' | 'custom' = 'select';
  @Input() items: string[] = [];
  @Input() placeholder: string = 'Select';
  private _selectedItem: string = '';
  @Input()
  set selectedItem(value: string) {
    this._selectedItem = this.normalizeDropdownText(value) ?? '';
  }
  get selectedItem(): string {
    return this._selectedItem;
  }
  @Input() backGround: string = 'white';
  @Input() borderColor: string = 'none';
  @Input() width?: string;
  @Input() isCallingFromAdvanceDateFilter: boolean = false;
  @Input() maxDisplayLength: number = 20;
  @Input() alignRight: boolean = false;
  @Input() alignRightOffsetY: number = -50;
  @Input() menuOffsetY: number = -1;
  @Input() menuMinWidth: number = 155;
  @Input() enableTrim: boolean = false;

  // Enables fixed positioning for the menu (used in mobile tab navigation and other overflow containers).
  @Input() useFixedPosition: boolean = true;

  // Open dropdown upward instead of downward (useful for pagination at bottom of screen)
  @Input() openUpward: boolean = false;

  // Always show placeholder text in trigger (useful for parent-child tab navigation).
  @Input() alwaysShowPlaceholder: boolean = false;

  // Icon type: 'chevron' (default) or 'kebab' (3 vertical dots menu icon)
  @Input() iconType: 'chevron' | 'kebab' = 'chevron';

  // Show active indicator dot
  @Input() hasActiveIndicator: boolean = false;
  @Input() nonSelectableItems: string[] = [];

  // Enable search/filter inside the trigger when filterType is 'select'
  @Input() searchable: boolean = false;

  @Input() multipleSelection: boolean = false;
  private _selectedItems: string[] = [];
  @Input()
  set selectedItems(value: string[]) {
    this._selectedItems = Array.isArray(value)
      ? value.map(item => this.normalizeDropdownText(item) ?? item)
      : [];
  }
  get selectedItems(): string[] {
    return this._selectedItems;
  }
  // Show an × button on each selected badge pill to remove it without opening the dropdown
  @Input() showRemoveBadge: boolean = false;
  @Output() itemSelected = new EventEmitter<string>();
  @Output() itemsSelected = new EventEmitter<string[]>();

  @ViewChild('menuEl') private menuEl?: ElementRef<HTMLElement>;
  @ViewChild('selectMenu') private selectMenuEl?: ElementRef<HTMLElement>;
  @ViewChild('searchInput') private searchInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('multiSearchInput') private multiSearchInputRef?: ElementRef<HTMLInputElement>;

  searchText: string = '';

  get filteredItems(): string[] {
    if (!this.searchable || !this.searchText.trim()) return this.items;
    const query = this.searchText.toLowerCase();
    return this.items.filter(item => item.toLowerCase().includes(query));
  }

  isDropdownOpen = false;
  menuStyle: { [key: string]: string } = {};
  highlightedIndex: number = -1;

  private dropdownId: string;
  private closeOthersHandler: (event: CustomEvent) => void;
  private captureClickHandler = (event: Event) => this.onDocumentClick(event);
  private needsPositionUpdate = false;

  private portalAttached = false;
  private originalParent: Node | null = null;
  private originalNextSibling: Node | null = null;
  private scrollHandler: ((event: Event) => void) | null = null;
  private shouldPreventAutoOpen = false;
  private scrollCloseGraceUntil = 0; // Timestamp: ignore scroll-close events before this time

  private cdr = inject(ChangeDetectorRef);

  constructor(private elementRef: ElementRef) {
    this.dropdownId = `dropdown-${++dropdownIdCounter}`;
    this.closeOthersHandler = this.handleCloseOthers.bind(this);
  }

  ngOnInit(): void {
    document.addEventListener('close-other-dropdowns', this.closeOthersHandler as EventListener);
    // Use capture phase so parent stopPropagation() cannot block this listener
    document.addEventListener('click', this.captureClickHandler, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('close-other-dropdowns', this.closeOthersHandler as EventListener);
    document.removeEventListener('click', this.captureClickHandler, true);
    this.removeScrollListener();
    this.detachMenuFromBody();
  }

  ngAfterViewChecked(): void {
    if (this.needsPositionUpdate && this.isDropdownOpen && this.useFixedPosition) {
      this.updateFixedPosition();
      this.needsPositionUpdate = false;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isDropdownOpen && this.useFixedPosition) {
      this.needsPositionUpdate = true;
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    if (this.isDropdownOpen && this.useFixedPosition) {
      this.needsPositionUpdate = true;
    }
  }

  private handleCloseOthers(event: CustomEvent): void {
    if (event.detail?.id !== this.dropdownId) {
      this.closeDropdown();
    }
  }

  toggleDropdown(): void {
    if (this.isDropdownOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  private openDropdown(): void {
    if (this.isDropdownOpen) return;
    document.dispatchEvent(
      new CustomEvent('close-other-dropdowns', {
        detail: { id: this.dropdownId },
      }),
    );
    this.isDropdownOpen = true;
    this.resetHighlightedIndex();

    if (this.searchable && this.filterType === 'select') {
      setTimeout(() => {
        if (this.multipleSelection) {
          this.multiSearchInputRef?.nativeElement?.focus();
        } else {
          this.searchInputRef?.nativeElement?.focus();
        }
      }, 50);
    }

    if (this.useFixedPosition && this.filterType === 'custom') {
      this.attachMenuToBody();
      // Allow 150ms grace period so that a scroll event triggered by the touch
      // that opened the dropdown doesn't immediately close it on mobile.
      this.scrollCloseGraceUntil = Date.now() + 150;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.updateFixedPosition();
          this.cdr.detectChanges();
          this.addScrollListener();
        });
      });
    }
  }

  private closeDropdown(): void {
    if (!this.isDropdownOpen) return;
    this.isDropdownOpen = false;
    this.highlightedIndex = -1;
    this.searchText = '';
    this.menuStyle = {};
    this.removeScrollListener();
    this.detachMenuFromBody();
  }

  private updateFixedPosition(): void {
    const trigger = this.elementRef.nativeElement.querySelector('.dropdown-trigger') as HTMLElement | null;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();

    // When the portal menu has zoom applied (e.g. zoom:0.8), position:fixed coordinates
    // are interpreted in the zoomed space, so top:X renders at X*zoom visually.
    // Divide coords by zoom so the menu lands at the correct viewport position.
    const zoom = this.getPortalMenuZoom();
    const s = 1 / zoom; // scale factor to compensate

    if (this.alignRight) {
      this.menuStyle = {
        position: 'fixed',
        top: `${(rect.bottom + this.alignRightOffsetY) * s}px`,
        right: `${(window.innerWidth - rect.right) * s}px`,
        left: 'auto',
        'min-width': `${Math.max(this.menuMinWidth, rect.width) * s}px`,
        'border-radius': '12px',
        'z-index': '3000000',
      };
      return;
    }

    // Handle upward opening (useful for pagination dropdowns at bottom of screen)
    if (this.openUpward) {
      this.menuStyle = {
        position: 'fixed',
        bottom: `${(window.innerHeight - rect.top + 4) * s}px`,
        left: `${rect.left * s}px`,
        width: `${rect.width * s}px`,
        'min-width': `${Math.max(this.menuMinWidth, rect.width) * s}px`,
        'border-radius': '9px',
        'z-index': '3000000',
      };
      return;
    }

    this.menuStyle = {
      position: 'fixed',
      top: `${(rect.bottom + this.menuOffsetY) * s}px`,
      left: `${rect.left * s}px`,
      width: `${rect.width * s}px`,
      'min-width': `${rect.width * s}px`,
      'z-index': '3000000',
    };
  }

  /** Returns the CSS zoom level applied to .common-dropdown-portal-menu (from styles.scss media query). */
  private getPortalMenuZoom(): number {
    const appLayout = document.querySelector('app-layout') as HTMLElement | null;
    if (!appLayout) return 1;
    const zoom = parseFloat(window.getComputedStyle(appLayout).getPropertyValue('zoom'));
    return isNaN(zoom) || zoom === 0 ? 1 : zoom;
  }

  // Kept for future diagnostics if needed.
  private getFixedPositionContextElement(trigger: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = trigger.parentElement;

    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const createsFixedContext =
        style.transform !== 'none' ||
        style.perspective !== 'none' ||
        style.filter !== 'none' ||
        style.backdropFilter !== 'none' ||
        style.contain === 'paint' ||
        style.willChange.includes('transform');

      if (createsFixedContext) return current;
      current = current.parentElement;
    }

    return null;
  }

  private attachMenuToBody(): void {
    if (this.portalAttached) return;
    const menuNode = this.menuEl?.nativeElement;
    if (!menuNode) return;

    this.originalParent = menuNode.parentNode;
    this.originalNextSibling = menuNode.nextSibling;
    document.body.appendChild(menuNode);
    this.portalAttached = true;
  }

  private detachMenuFromBody(): void {
    if (!this.portalAttached) return;
    const menuNode = this.menuEl?.nativeElement;
    if (!menuNode || !this.originalParent) return;

    if (this.originalNextSibling) {
      this.originalParent.insertBefore(menuNode, this.originalNextSibling);
    } else {
      this.originalParent.appendChild(menuNode);
    }

    this.portalAttached = false;
    this.originalParent = null;
    this.originalNextSibling = null;
  }

  private addScrollListener(): void {
    this.removeScrollListener();
    this.scrollHandler = (event: Event) => {
      if (!this.isDropdownOpen) return;

      // Don't close if scroll originated from inside the portal menu itself
      // (e.g. user scrolling through a long list of dropdown items).
      const scrollTarget = event.target as Node | null;
      if (scrollTarget && this.menuEl?.nativeElement?.contains(scrollTarget)) {
        return;
      }

      // Grace period: on mobile, a touch that opens the dropdown can also
      // trigger a scroll event on a parent container. Ignore scroll events
      // that fire within 150ms of opening.
      if (Date.now() < this.scrollCloseGraceUntil) {
        return;
      }

      this.closeDropdown();
      this.cdr.detectChanges();
    };
    // Use capture phase to catch scroll events on any container, not just window
    document.addEventListener('scroll', this.scrollHandler, true);
  }

  private removeScrollListener(): void {
    if (this.scrollHandler) {
      document.removeEventListener('scroll', this.scrollHandler, true);
      this.scrollHandler = null;
    }
  }

  selectItem(item: string): void {
    const normalized = this.normalizeDropdownText(item) ?? item;
    this.selectedItem = normalized;
    this.itemSelected.emit(normalized);
    this.highlightedIndex = -1;
    this.closeDropdown();
  }

  onTriggerFocus(): void {
    if (this.shouldPreventAutoOpen) {
      return;
    }
    if (!this.isDropdownOpen) {
      this.openDropdown();
    }
  }

  onTriggerMouseDown(): void {
    this.shouldPreventAutoOpen = true;
    setTimeout(() => {
      this.shouldPreventAutoOpen = false;
    }, 0);
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      if (this.isDropdownOpen) {
        this.closeDropdown();
        // Do not preventDefault — let Tab move focus to the next element naturally
      }
      return;
    }

    const openKeys = ['Enter', ' ', 'ArrowDown'];
    if (!this.isDropdownOpen && openKeys.includes(event.key)) {
      event.preventDefault();
      this.openDropdown();
      return;
    }

    // Multi-select: if dropdown is open and trigger has focus (search input lost focus),
    // delegate Arrow/Enter/Escape to the same multi-select navigation
    if (this.isDropdownOpen && this.multipleSelection) {
      const navKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'];
      if (navKeys.includes(event.key)) {
        event.preventDefault();
        // Re-focus search input first so subsequent key events route correctly
        this.multiSearchInputRef?.nativeElement?.focus();
        this.onSearchInputKeydown(event);
      }
      return;
    }

    this.handleNavigationKey(event);
  }

  private handleNavigationKey(event: KeyboardEvent): void {
    if (!this.isDropdownOpen || !this.shouldUseKeyboardNavigation()) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveHighlight(-1);
        break;
      case 'Enter':
        event.preventDefault();
        this.selectHighlightedItem();
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
    }
  }

  private moveHighlight(direction: 1 | -1): void {
    if (!this.shouldUseKeyboardNavigation()) return;
    const items = this.filteredItems;
    if (!items || items.length === 0) {
      this.highlightedIndex = -1;
      return;
    }

    const currentIndex = this.highlightedIndex < 0 ? 0 : this.highlightedIndex;
    let nextIndex = currentIndex + direction;

    if (nextIndex < 0) {
      nextIndex = items.length - 1;
    } else if (nextIndex >= items.length) {
      nextIndex = 0;
    }

    this.highlightedIndex = nextIndex;
    this.scrollHighlightedIntoView();
  }

  private selectHighlightedItem(): void {
    if (!this.shouldUseKeyboardNavigation() || this.highlightedIndex < 0) return;
    const items = this.filteredItems;
    const target = items[this.highlightedIndex];
    if (target) {
      this.selectItem(target);
    }
  }

  private resetHighlightedIndex(): void {
    if (!this.shouldUseKeyboardNavigation()) {
      this.highlightedIndex = -1;
      return;
    }
    const items = this.filteredItems;
    this.highlightedIndex = items.length > 0 ? 0 : -1;
    this.scrollHighlightedIntoView();
  }

  private scrollHighlightedIntoView(): void {
    if (!this.shouldUseKeyboardNavigation() || this.highlightedIndex < 0 || !this.selectMenuEl) {
      return;
    }

    requestAnimationFrame(() => {
      const menu = this.selectMenuEl?.nativeElement;
      const items = menu?.querySelectorAll<HTMLElement>('.dropdown-item');
      if (!menu || !items || items.length === 0) return;

      const clampedIndex = Math.min(this.highlightedIndex, items.length - 1);
      const target = items[clampedIndex];
      if (!target) return;

      const menuRect = menu.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      if (targetRect.top < menuRect.top || targetRect.bottom > menuRect.bottom) {
        target.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  private shouldUseKeyboardNavigation(): boolean {
    return this.filterType === 'select' && !this.multipleSelection;
  }

  onSearchInput(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
    this.resetHighlightedIndex();
  }

  onSearchInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      this.closeDropdown();
      // Do not preventDefault — let Tab move focus to the next element naturally
      return;
    }

    if (!this.multipleSelection) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveMultiHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveMultiHighlight(-1);
        break;
      case 'Enter':
        event.preventDefault();
        this.toggleMultiHighlightedItem();
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
    }
  }

  private moveMultiHighlight(direction: 1 | -1): void {
    const items = this.filteredItems;
    if (!items || items.length === 0) {
      this.highlightedIndex = -1;
      return;
    }

    if (this.highlightedIndex < 0) {
      this.highlightedIndex = direction > 0 ? 0 : items.length - 1;
    } else {
      const next = this.highlightedIndex + direction;
      if (next < 0) {
        // ArrowUp on first item → go back to search input
        this.highlightedIndex = -1;
        this.multiSearchInputRef?.nativeElement?.focus();
        return;
      } else if (next >= items.length) {
        // ArrowDown on last item — stay, don't wrap
        return;
      } else {
        this.highlightedIndex = next;
      }
    }

    this.scrollMultiHighlightedIntoView();
  }

  private toggleMultiHighlightedItem(): void {
    if (this.highlightedIndex < 0) return;
    const item = this.filteredItems[this.highlightedIndex];
    if (item && !this.isItemNonSelectable(item)) {
      this.updateMultiSelection(item);
    }
  }

  private scrollMultiHighlightedIntoView(): void {
    if (this.highlightedIndex < 0 || !this.selectMenuEl) return;
    requestAnimationFrame(() => {
      const menu = this.selectMenuEl?.nativeElement;
      const items = menu?.querySelectorAll<HTMLElement>('.dropdown-item');
      if (!menu || !items || items.length === 0) return;
      const target = items[this.highlightedIndex];
      if (!target) return;
      const menuRect = menu.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      if (targetRect.top < menuRect.top || targetRect.bottom > menuRect.bottom) {
        target.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  isItemNonSelectable(item: string): boolean {
    return this.nonSelectableItems?.includes(item) ?? false;
  }

  isItemChecked(item: string): boolean {
    const normalized = this.normalizeValue(item);
    return this.selectedItems.some(selected => this.normalizeValue(selected) === normalized);
  }

  toggleMultiItem(item: string, event: Event): void {
    event.stopPropagation();
    if (this.isItemNonSelectable(item)) return;
    this.updateMultiSelection(item);
  }

  onMultiItemTouchEnd(item: string, event: Event): void {
    event.stopPropagation();
    (event as TouchEvent).preventDefault?.();
    if (this.isItemNonSelectable(item)) return;
    this.updateMultiSelection(item);
  }

  onMultiCheckboxChange(item: string): void {
    if (this.isItemNonSelectable(item)) return;
    this.updateMultiSelection(item);
  }

  removeBadgeItem(item: string, event: Event): void {
    event.stopPropagation();
    const normalized = this.normalizeValue(item);
    this.selectedItems = this.selectedItems.filter(selected => this.normalizeValue(selected) !== normalized);
    this.itemsSelected.emit([...this.selectedItems]);
  }

  private updateMultiSelection(item: string): void {
    const normalized = this.normalizeValue(item);
    const isSelected = this.selectedItems.some(selected => this.normalizeValue(selected) === normalized);
    let updated = this.selectedItems.filter(selected => this.normalizeValue(selected) !== normalized);
    if (!isSelected) {
      updated = [...updated, normalized];
    }
    this.selectedItems = updated;
    this.itemsSelected.emit([...this.selectedItems]);
  }

  getMultiDisplayText(): string {
    if (this.selectedItems.length === 0) return this.getDisplayText(this.placeholder);
    if (this.selectedItems.length === 1) return this.getDisplayText(this.selectedItems[0]);
    return `${this.selectedItems.length} selected`;
  }

  onItemClick(item: string, event: Event): void {
    event.stopPropagation();
    if (this.isItemNonSelectable(item)) return;
    this.selectItem(item);
  }

  onItemTouchEnd(item: string, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.isItemNonSelectable(item)) return;
    this.selectItem(item);
  }

  onDocumentClick(event: Event): void {
    if (!this.isDropdownOpen) return;

    const targetNode = event.target as Node;
    const clickedInsideHost = this.elementRef.nativeElement.contains(targetNode);
    const clickedInsideMenu = this.portalAttached && !!this.menuEl?.nativeElement.contains(targetNode);

    if (!clickedInsideHost && !clickedInsideMenu) {
      this.closeDropdown();
      this.cdr.detectChanges();
    }
  }

  getDisplayText(text: string, maxLength?: number): string {
    if (!text) return '';
    if (!this.enableTrim) {
      return text;
    }
    const limit = maxLength !== undefined ? maxLength : this.maxDisplayLength;
    return text.length > limit ? text.substring(0, limit) + '...' : text;
  }

  private normalizeDropdownText(value: string | null | undefined): string | null {
    if (value === undefined || value === null) return null;
    const normalized = value.replace(/\s+/g, ' ').trim();
    return normalized;
  }

  private normalizeValue(value: string | null | undefined): string {
    const normalized = this.normalizeDropdownText(value);
    return normalized ?? '';
  }
}
